




















































































































import _ from 'lodash'
import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop, Watch} from 'vue-property-decorator';
import { AuthorizationItem } from '@/modules/authorization/model/AuthorizationItem'

class SearchItemPrototype extends AuthorizationItem {
    public serialize () {
        return {}
    }
}

/**
 * Árbol de elementos de seguridad
 *
 * Los elementos de la política de seguridad (Permisos y Roles) de Artisan tienen varias
 * propiedades en común. Una de ellas es que pueden contener elementos hijos, formando
 * asi una jerarquía de elementos.
 *
 * Este componente permite visualizar dichos elementos en forma de árbol.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component
export default class AuthorizationItemTree extends Vue {
    public $refs: Vue['$refs'] & {
        tree: {
            updateAll: (val: boolean) => void,
        },
    }

    /**
     * Título mostrado en la cabecera del árbol
     */
    @Prop({type: String, default: 'Árbol de elementos'})
    public title: string

    /**
     * Nombre del input que permite al usuario buscar elementos
     */
    @Prop({type: String, default: 'Buscar elementos'})
    public searchInputLabel: string

    /**
     * Nodo raíz del árbol de elementos de seguridad
     */
    @Prop({type: [Object], required: true})
    public tree: AuthorizationItem

    /**
     * Deshabilita el árbol
     *
     * Impide al usuario seleccionar nodos o realizar búsquedas
     */
    @Prop({type: Boolean, default: false})
    public disabled: boolean

    /**
     * Filtro
     *
     * Permite definir una función personalizada para realizar búsquedas de nodos
     * a través del árbol de permisos
     */
    @Prop({type: Function, default: null})
    public filter: ((search: string) => AuthorizationItem[]) | null

    /**
     * Permite definir una función encargada de indicar al árbol que nodos deshabilitar
     */
    @Prop({type: Function, default: null})
    public filterSelect: (path: string) => boolean

    /**
     * Ocultar cabecera
     *
     * Si se activa el componente no renderizará la barra de herramientas superior
     * con el título y los botones de expandir y recargar el árbol
     */
    @Prop({type: Boolean, default: false})
    public hideHeader: boolean

    /**
     * Ocultar barra de búsqueda
     *
     * Si se activa el componente no renderizará la barra de busqueda superior
     * que permite buscar nodos
     */
    @Prop({type: Boolean, default: false})
    public hideSearchBar: boolean

    /**
     * Ocupar alto máximo
     *
     * Si se activa el componente ocupará todo el alto posible del contenedor
     * padre
     */
    @Prop({type: Boolean, default: true})
    public fullHeight: boolean

    /**
     * Cargador
     *
     * Acepta una función que será invocada cuando el usuario pulse el botón de
     * actualización del árbol
     */
    @Prop({type: Function, default: null})
    public loader: () => Promise<any>

    /**
     * Activable
     *
     * Define si el usuario podrá activar un nodo haciendo click sobre el
     */
    @Prop({type: Boolean, default: true})
    public activatable: boolean

    /**
     * Seleccionable
     */
    @Prop({type: Boolean, default: false})
    public selectable: boolean

    @Prop({type: Boolean, default: false})
    public editable: boolean

    /**
     * Permite definir que icono será mostrado antes del nombre del nodo cuando
     * este es un nodo único, sin hijos.
     */
    @Prop({type: String, default: 'mdi-pound-box'})
    public singleNodeIcon: string


    /**
     * Permite especificar si los nodos del árbol se pueden eliminar. Si se
     * activa esta opción se mostrará en cada nodo y botón para eliminar.
     */
    @Prop({type: Boolean, default: false})
    public deletable: boolean

    public search = ''
    public reloading = false

    public activeNodes: string[] = []
    public selection: string[] = []

    public exactSelection: string[] = []

    /**
     * Expandido
     *
     * Indica si todos los árbol se encuentra abiertos al mismo tiempo
     */
    public expanded = false

    /**
     * Nodos abiertos por el usuario
     */
    public userOpenedNodes: string[] = []

    private searchNodePrototype = Object.assign(new SearchItemPrototype(), {
        key: 'search',
    })

    /**
     * Permite acceder al permiso raíz del árbol
     */
    public get root (): AuthorizationItem | null {
        return this.tree
    }

    @Watch('tree')
    public onTreeChange () {
        this.openRootNode()
    }

    /**
     * Comprueba si el elemento argumentado es la raíz del árbol de elementos
     */
    public isRoot (item: AuthorizationItem) {
        return this.root
            ? item.path === this.root.path
            : false
    }

    public toggleExpand () {
        this.expanded = !this.expanded;
        this.$refs.tree.updateAll(this.expanded)
    }

    /**
     * Indica al árbol que nodos se encuentra abiertos
     */
    public get openNodes () {
        return this.search ? ['/search'] : this.userOpenedNodes
    }

    public set openNodes (val: string[]) {
        this.userOpenedNodes = val
    }

    public get searched () {
        if (!this.search) {
            return []
        }

        // Si no existe ninguna función de filtrado personalizada se utilizará
        // el mecanismo de búsqueda por defecto
        const result = this.filter
            ? this.filter(this.search)
            : this.root!.findOccurrences(this.search)

        return [
            Object.assign(this.searchNodePrototype, {
                title: `Buscar "${this.search}"`,
                children: result,
            }),
        ]
    }

    /**
     * Abre el árbol de elementos hasta el nodo solicitado
     */
    public navigateToNode (path: string) {
        this.search = ''

        const segments = path.split('/')
        const paths: string[] = []

        segments.forEach((segment, i) => {
            const dir = paths[i - 1]
            paths.push((dir ? `${dir}/` : '') + segment)
        })

        this.openNodes = paths
        this.activeNodes = [path]
    }

    /**
     * Seleccionar
     *
     * Notifica al sistema que el usuario ha seleccionado un nodo dentro del árbol
     * de elementos
     */
    public activeNode (items: AuthorizationItem[]) {
        // No es un array o esta vacío, no se notifica
        if (!Array.isArray(items) || !items.length) {
            return
        }

        this.$emit('update:active', items[0])
    }

    /**
     * Recarga el árbol
     */
    public async reload () {
        this.reloading = true
        // Notifica al componente padre que el usuario ha solicitado recargar
        // el árbol del permisos
        this.$emit('reload')
        if (this.loader) {
            await this.loader()
        }

        this.reloading = false
    }

    /**
     * Selecciona un nodo
     *
     * @param path
     * @param descendants
     */
    public selectNode (path: string, descendants = true) {
        if (!this.root) {
            throw new Error('El árbol no tiene ningún nodo raíz')
        }

        const node = this.root.findByPath(path)
        if (!node) {
            throw new Error(`No existe ningún nodo con la ruta ${path}`)
        }

        this.selection.push(...(descendants ? node.flattenedPaths : [node.path]))
        this.exactSelection.push(node.path)

        this.$emit('select:exact', this.exactSelection)
    }

    /**
     * Elimina un nodo de la lista de nodos seleccionados
     */
    public unselectNode (path: string) {
        if (!this.root) {
            throw new Error('El árbol no tiene ningún nodo raíz')
        }

        const node = this.root.findByPath(path)
        if (!node) {
            throw new Error(`No existe ningún nodo con la ruta ${path}`)
        }

        const paths = node.flattenedPaths
        paths.forEach(path => {
            const index = this.selection.findIndex(s => s === path)
            if (index === -1) {
                return
            }

            this.selection.splice(index, 1)
        })

        // También se elimina de la selección exacta del usuario
        const index = this.exactSelection.findIndex(s => s === path)
        if (index !== -1) {
            this.exactSelection.splice(index, 1)
        }

        this.$emit('select:exact', this.exactSelection)
    }

    /**
     * Comprueba si el checkbox de un nodo es indeterminado
     */
    public nodeIsIndeterminated (path: string) {
        const node = this.root!.findByPath(path)
        if (!node) {
            throw new Error(`No existe el nodo con la ruta ${path}`)
        }

        const nodePathList = node.flattenedPaths
        return !this.nodeIsSelected(node.path)
                && _.inRange(_.intersection(this.selection, nodePathList).length, 1, nodePathList.length + 1)
    }

    /**
     * Comprueba sin un nodo se encuentra seleccionado por el usuario
     * @param path
     */
    public nodeIsSelected (path: string) {
        return !!this.selection.find(s => s === path)
    }

    /**
     * Comprueba, dado una determinada ruta de nodo si el nodo padre se encuentra
     * seleccionado explícitamente por el usuario
     *
     * @param path
     */
    public parentIsSelected (path: string): boolean {
        if (!path) {
            return false
        }

        const node = this.tree.findByPath(path)
        if (!node) {
            throw new Error(`El nodo con la ruta ${path} no existe en el árbol`)
        }

        const parentPath = node.parentPath

        return (!!this.exactSelection.find(s => s === parentPath)) || this.parentIsSelected(parentPath)
    }

    /**
     * Abre el nodo padre
     */
    public openRootNode () {
        if (!this.root) {
            return
        }

        // tslint:disable-next-line:no-unused-expression
        this.userOpenedNodes.find(n => n === this.root!.path) || this.userOpenedNodes.push(this.root!.path)
    }

    public clearSelection () {
        this.exactSelection = []
        this.selection = []
    }

    public mounted () {
        this.openRootNode()
    }
}
